import { CanvasItem, Connection, ConnectionSide, NODE_WIDTH, NODE_HEIGHT, EMPLOYEE_NODE_HEIGHT } from "./types";

interface OrgChartConnectionsProps {
  canvasItems: CanvasItem[];
  connections: Connection[];
  canvasWidth: number;
  canvasHeight: number;
  onRemoveConnection: (fromId: string, toId: string) => void;
}

function getConnectionPoint(item: CanvasItem, side: ConnectionSide) {
  const height = item.type === 'department' ? NODE_HEIGHT : EMPLOYEE_NODE_HEIGHT;
  switch (side) {
    case 'top':    return { x: item.x + NODE_WIDTH / 2, y: item.y };
    case 'right':  return { x: item.x + NODE_WIDTH, y: item.y + height / 2 };
    case 'bottom': return { x: item.x + NODE_WIDTH / 2, y: item.y + height };
    case 'left':   return { x: item.x, y: item.y + height / 2 };
  }
}

export function OrgChartConnections({ canvasItems, connections, canvasWidth, canvasHeight, onRemoveConnection }: OrgChartConnectionsProps) {
  const paths = connections.map(conn => {
    const fromItem = canvasItems.find(i => i.id === conn.fromId);
    const toItem = canvasItems.find(i => i.id === conn.toId);
    if (!fromItem || !toItem) return null;

    const from = getConnectionPoint(fromItem, conn.fromSide);
    const to = getConnectionPoint(toItem, conn.toSide);

    // Smooth bezier curve
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const isVertical = conn.fromSide === 'bottom' || conn.fromSide === 'top';
    
    let cp1x, cp1y, cp2x, cp2y;
    if (isVertical) {
      const offset = Math.max(Math.abs(dy) * 0.4, 40);
      cp1x = from.x;
      cp1y = conn.fromSide === 'bottom' ? from.y + offset : from.y - offset;
      cp2x = to.x;
      cp2y = conn.toSide === 'top' ? to.y - offset : to.y + offset;
    } else {
      const offset = Math.max(Math.abs(dx) * 0.4, 40);
      cp1x = conn.fromSide === 'right' ? from.x + offset : from.x - offset;
      cp1y = from.y;
      cp2x = conn.toSide === 'left' ? to.x - offset : to.x + offset;
      cp2y = to.y;
    }

    const path = `M ${from.x} ${from.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${to.x} ${to.y}`;

    return { path, from, to, fromId: conn.fromId, toId: conn.toId };
  }).filter(Boolean);

  return (
    <svg className="absolute inset-0 pointer-events-none" style={{ width: canvasWidth, height: canvasHeight }}>
      <defs>
        <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="hsl(var(--primary))" opacity="0.7" />
        </marker>
      </defs>
      {paths.map((conn, i) => conn && (
        <g key={i} className="pointer-events-auto">
          {/* Invisible wider path for easier clicking */}
          <path
            d={conn.path}
            fill="none"
            stroke="transparent"
            strokeWidth="16"
            className="cursor-pointer"
            onClick={() => onRemoveConnection(conn.fromId, conn.toId)}
          />
          <path
            d={conn.path}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="2.5"
            strokeLinecap="round"
            markerEnd="url(#arrowhead)"
            className="cursor-pointer hover:stroke-destructive transition-colors"
            style={{ filter: 'drop-shadow(0 1px 2px hsl(var(--primary) / 0.2))' }}
            onClick={() => onRemoveConnection(conn.fromId, conn.toId)}
          />
        </g>
      ))}
    </svg>
  );
}
